/**
 * Icon — re-export of Lucide icons with project conventions.
 *
 * Why a wrapper:
 *  - Single import path for the rest of the app (`@/components/Icon`)
 *  - Project-default stroke width 1.75 (Lucide default 2 is too heavy at small sizes)
 *  - `neon` prop for adding our signature cyan/lime/pink glow
 *  - `size` shorthand: 'xs' | 'sm' | 'md' | 'lg' → 12/14/16/20px
 *
 * Names chosen on import — only the icons we use are bundled (tree-shaken).
 */
import {
  AlertTriangle,
  Bot,
  ChevronRight,
  Check,
  Clock,
  Eye,
  Flame,
  Hammer,
  Headphones,
  Keyboard,
  Library,
  Lightbulb,
  LogOut,
  Mic,
  PartyPopper,
  Puzzle,
  Search,
  Shield,
  Sparkles,
  Square,
  Star,
  Stethoscope,
  Sun,
  Target,
  Turtle,
  User,
  Volume2,
  VolumeX,
  Wrench,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/** Map our project token → Lucide component. Add here as we need them. */
export const ICONS = {
  // Modes
  puzzle: Puzzle,
  typing: Keyboard,
  speaking: Mic,
  // Audio
  listen: Volume2,
  mute: VolumeX,
  headphones: Headphones,
  slow: Turtle,
  // Nav / sections
  today: Sun,
  courses: Library,
  vocab: Sparkles,
  create: Hammer,
  mistakes: Stethoscope,
  profile: User,
  admin: Shield,
  pricing: Star,
  // States / actions
  check: Check,
  warn: AlertTriangle,
  peek: Eye,
  hint: Lightbulb,
  ai: Bot,
  search: Search,
  target: Target,
  reset: Wrench,
  close: X,
  chevron: ChevronRight,
  logout: LogOut,
  square: Square,
  // Vibes
  streak: Flame,
  perfect: Zap,
  celebrate: PartyPopper,
  pro: Star,
  clock: Clock,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

const SIZE_PX: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number> = {
  xs: 12, sm: 14, md: 16, lg: 20, xl: 28,
};

interface IconProps {
  name: IconName;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  className?: string;
  strokeWidth?: number;
  /** Adds drop-shadow neon glow. Color follows currentColor by default. */
  neon?: boolean;
  /** Optional aria-label — purely decorative icons default to aria-hidden. */
  label?: string;
}

export function Icon({
  name,
  size = 'md',
  className = '',
  strokeWidth = 1.75,
  neon = false,
  label,
}: IconProps): JSX.Element {
  const Cmp = ICONS[name];
  const px = typeof size === 'number' ? size : SIZE_PX[size];
  return (
    <Cmp
      size={px}
      strokeWidth={strokeWidth}
      aria-hidden={label === undefined}
      aria-label={label}
      className={[
        'inline-block shrink-0',
        neon ? '[filter:drop-shadow(0_0_6px_currentColor)]' : '',
        className,
      ].filter(Boolean).join(' ')}
    />
  );
}
